import { translate } from '@jsverse/transloco';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import { subscribeOnce } from '@tmx/core';
import { AccountsApi, TripManagementSettingsApi } from '@tmx/data-access';
import { combineLatest, of, switchMap, tap } from 'rxjs';

import { getErrorContext } from '../../utils/get-error-context';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import { saveSelectedPageSize } from '../../utils/save-selected-page-size';
import { setBrazeAttribute } from '../ams/ams.actions';
import { updateToast } from '../toasts.repository';
import {
  checkWeatherAlertsEnabled,
  initializeAppSettings,
  loadAccountLanguage,
  loadCompanyEmailSettings,
  loadCompanyLicenseFeatures,
  loadCompanySettings,
  loadCountryCodes,
  loadLanguages,
  loadPartnerSettings,
  saveCompanyEmailSettings,
  setCompanySettings,
  setDataTablePageSize,
  setLinkedDataState,
  switchAccountResetAppSettings,
  updateAccountLanguage,
} from './app-settings.actions';
import {
  checkedWeatherAlertsAreEnabled$,
  companyLicenseFeaturesInitialized$,
  companyLicenseFeaturesSnapshot,
  companySettingsInitialized$,
  partnerSettingsInitialized$,
  resetAppSettingsStore,
  trackAppSettingsRequests,
  updateAppSettingsState,
  updateCompanyEmailSettings,
  updateCompanyLicenseFeatures,
  updateCompanySettings,
  updateCountryCodes,
  updateDataTablePageSize,
  updateDefaultLanguage,
  updateLanguages,
  updateLinkedDataState,
  updatePartnerSettings,
  updateWeatherAlertsEnabled,
} from './app-settings.repository';

const TRACKJS_SOURCE = 'app-settings effects';

const initializeAppSettings$ = createEffect((actions) =>
  actions.pipe(
    ofType(initializeAppSettings),
    tapResult(() => {
      dispatch(loadCompanySettings());
      dispatch(loadPartnerSettings());
      dispatch(loadCompanyLicenseFeatures());

      subscribeOnce(
        combineLatest([
          companySettingsInitialized$,
          partnerSettingsInitialized$,
          companyLicenseFeaturesInitialized$.pipe(
            switchMap((initialized) => {
              if (initialized && companyLicenseFeaturesSnapshot().weather) {
                dispatch(checkWeatherAlertsEnabled());
                return checkedWeatherAlertsAreEnabled$;
              }

              return of(initialized);
            })
          ),
        ]),
        {
          next: (results) => {
            const allSettingsInitialized = results.every(
              (isInitialized) => isInitialized
            );
            updateAppSettingsState(
              allSettingsInitialized ? 'initialized' : 'failed'
            );
            setTimeout(() => {
              setLinkedDataState();
            });
          },
        }
      );
    })
  )
);

const loadCompanySettings$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadCompanySettings),
    switchMap(() =>
      AccountsApi.getCompanySettings().pipe(
        trackAppSettingsRequests(['fetch-company-settings']),
        tapResult(
          ({ data }) => {
            updateCompanySettings(data);
          },
          (error) => {
            updateCompanySettings(null);
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading Company Settings',
              getErrorContext(error)
            );
          }
        )
      )
    )
  )
);

const updateAccountLanguage$ = createEffect((actions) =>
  actions.pipe(
    ofType(updateAccountLanguage),
    switchMap(({ languageId }) =>
      AccountsApi.postPreferencesAccountLanguage(languageId).pipe(
        tapResult(
          (language) => {
            updateDefaultLanguage(language.languageId);
            updateToast({
              type: 'success',
              content: translate(
                'accountSettings.mapSchedulerNotifications.companySettingsSaved'
              ),
            });

            dispatch(
              setBrazeAttribute({
                key: 'account_preferred_language',
                value: language.languageId,
              })
            );
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error saving Company Settings',
              getErrorContext(error)
            );
            updateToast({
              type: 'danger',
              content: translate(
                'accountSettings.mapSchedulerNotifications.companySettingsNotSaved'
              ),
            });
          }
        )
      )
    )
  )
);

const setCompanySettings$ = createEffect((actions) =>
  actions.pipe(
    ofType(setCompanySettings),
    switchMap(({ companySettings }) =>
      AccountsApi.postCompanySettings(companySettings).pipe(
        trackAppSettingsRequests(['update-company-settings']),
        tapResult(
          () => {
            updateCompanySettings(companySettings);
            updateToast({
              type: 'success',
              content: translate(
                'manageAccount.manageAccount.companySettingsSaved'
              ),
            });
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error saving Company Settings',
              getErrorContext(error)
            );
            updateToast({
              type: 'danger',
              content: translate(
                'manageAccount.manageAccount.companySettingsNotSaved'
              ),
            });
          }
        )
      )
    )
  )
);

const loadPartnerSettings$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadPartnerSettings),
    switchMap(() =>
      AccountsApi.getPartnerSettings().pipe(
        trackAppSettingsRequests(['partner-settings']),
        tapResult(
          (response) => {
            updatePartnerSettings(response);
          },
          (error) => {
            updatePartnerSettings(null);
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading Partner Settings',
              getErrorContext(error)
            );
          }
        )
      )
    )
  )
);

const switchAccountResetAppSettings$ = createEffect((actions) =>
  actions.pipe(
    ofType(switchAccountResetAppSettings),
    tapResult(() => {
      resetAppSettingsStore();
    })
  )
);

const setDataTablePageSize$ = createEffect((actions) =>
  actions.pipe(
    ofType(setDataTablePageSize),
    tapResult(({ dataTablePageSize }) => {
      saveSelectedPageSize(dataTablePageSize);
      updateDataTablePageSize(dataTablePageSize);
    })
  )
);

const loadCompanyEmailSettings$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadCompanyEmailSettings),
    switchMap(() =>
      AccountsApi.getCompanyEmailSettings().pipe(
        trackAppSettingsRequests(['fetch-company-email-settings']),
        tapResult(
          (response) => {
            updateCompanyEmailSettings(response);
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading Company Email Settings',
              getErrorContext(error)
            );
          }
        )
      )
    )
  )
);

const saveCompanyEmailSettings$ = createEffect((actions) =>
  actions.pipe(
    ofType(saveCompanyEmailSettings),
    switchMap(({ companyEmailSettings }) =>
      AccountsApi.putCompanyEmailSettings(companyEmailSettings).pipe(
        trackAppSettingsRequests(['save-company-email-settings']),
        tapResult(
          () => {
            updateToast({
              type: 'success',
              content: translate(
                'accountSettings.mapSchedulerNotifications.notificationsSaved'
              ),
            });
            updateCompanyEmailSettings(companyEmailSettings);
          },
          (error) => {
            updateToast({
              type: 'danger',
              content: translate(
                'accountSettings.mapSchedulerNotifications.notificationsNotSaved'
              ),
            });
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error saving Company Email Settings',
              getErrorContext(error)
            );
          }
        )
      )
    )
  )
);

const loadCountryCodes$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadCountryCodes),
    switchMap(() =>
      AccountsApi.getSupportCountryCodes().pipe(
        trackAppSettingsRequests(['fetch-country-codes']),
        tapResult((countryCodes) => {
          updateCountryCodes(countryCodes);
        })
      )
    )
  )
);

const loadLanguages$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadLanguages),
    switchMap(() =>
      AccountsApi.getPreferencesLanguages().pipe(
        trackAppSettingsRequests(['fetch-languages']),
        tapResult((languages) => {
          updateLanguages(languages);
        })
      )
    )
  )
);

const loadAccountLanguage$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadAccountLanguage),
    switchMap(() =>
      AccountsApi.getPreferencesAccountLanguage().pipe(
        trackAppSettingsRequests(['fetch-account-language']),
        tapResult((language) => {
          updateDefaultLanguage(language.languageId);
        })
      )
    )
  )
);

const setLinkedDataState$ = createEffect((actions) =>
  actions.pipe(
    ofType(setLinkedDataState),
    tap(() => {
      updateLinkedDataState();
    })
  )
);

const loadCompanyLicenseFeatures$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadCompanyLicenseFeatures),
    switchMap(() =>
      AccountsApi.getCompanyLicenseFeatures().pipe(
        trackAppSettingsRequests(['fetch-company-license-features']),
        tapResult((licenseFeatures) => {
          updateCompanyLicenseFeatures(licenseFeatures);
        })
      )
    )
  )
);

const checkWeatherAlertsEnabled$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkWeatherAlertsEnabled),
    switchMap(() =>
      TripManagementSettingsApi.getConfigurationSectionName(
        'weatherAlerts'
      ).pipe(
        trackAppSettingsRequests(['check-weather-alerts-enabled']),
        tapResult(
          ({ weatherAlerts }) => {
            const weatherAlertsEnabled =
              Boolean(weatherAlerts?.notificationEnabled) &&
              weatherAlerts?.weatherAlertsType === 2;
            updateWeatherAlertsEnabled(weatherAlertsEnabled);
          },
          () => {
            updateWeatherAlertsEnabled(false);
          }
        )
      )
    )
  )
);

const effects = [
  initializeAppSettings$,
  loadCompanySettings$,
  setCompanySettings$,
  loadPartnerSettings$,
  switchAccountResetAppSettings$,
  setDataTablePageSize$,
  loadCompanyEmailSettings$,
  saveCompanyEmailSettings$,
  loadCountryCodes$,
  loadLanguages$,
  loadAccountLanguage$,
  updateAccountLanguage$,
  setLinkedDataState$,
  loadCompanyLicenseFeatures$,
  checkWeatherAlertsEnabled$,
];

export function registerAppSettingsEffects() {
  registerEffects(effects);
}

export function deregisterAppSettingsEffects() {
  removeEffects(effects);
}

/**
 * t(accountSettings.mapSchedulerNotifications.notificationsNotSaved)
 * t(accountSettings.mapSchedulerNotifications.notificationsSaved)
 */
